import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { favoritesClient } from '@/api/clients/favoritesClient';
import { NetworkError, ServerError } from '@/api/errors';
import type { FavoriteEntityType } from '@/api/types/entityTypes';
import type { FavoriteId } from '@/api/types/favoriteTypes';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { isTokenAuthenticated } from '@/features/auth/isTokenAuthenticated';

export const useUserFavoriteIdsQueryKey = 'useFavoriteIdsQuery';

export function useUserFavoriteIdsQuery(options?: {
  staleTime?: number;
  filterBy?: FavoriteEntityType[];
}): UseQueryResult<FavoriteId[]> {
  const { accessToken } = useUserInfo();
  const isAuthenticated = isTokenAuthenticated(accessToken);

  return useQuery({
    retry: (failureCount, error) => {
      switch (error.constructor) {
        case NetworkError:
        case ServerError:
          return failureCount < 3;
      }

      return false;
    },
    ...(options?.staleTime !== undefined ? { staleTime: options.staleTime } : {}),
    // Watch accessToken, as it can mutate, ie, mvpd user adding profile
    queryKey: [useUserFavoriteIdsQueryKey, accessToken, options?.filterBy],
    queryFn: async () => {
      return await favoritesClient.getUserFavoriteIds(options?.filterBy);
    },
    // Should only be enabled for authenticated users
    enabled: isAuthenticated,
  });
}
